import { create } from 'zustand';

export type PromoCandidateStatus = 'pending' | 'eligible' | 'ineligible';

interface PromoCandidate {
	id: string;
	status: PromoCandidateStatus;
}

interface PromoStore {
	candidates: PromoCandidate[];
	winnerId: string | null;
	register: (id: string) => void;
	unregister: (id: string) => void;
	setStatus: (id: string, status: PromoCandidateStatus) => void;
}

const computeWinner = (candidates: PromoCandidate[]): string | null => {
	for (const candidate of candidates) {
		if (candidate.status === 'pending') return null;
		if (candidate.status === 'eligible') return candidate.id;
	}
	return null;
};

/**
 * Shared coordination store for promo modals. Each <PromoModal> registers
 * itself on mount (append order = JSX order = priority), reports its status
 * (`pending` while its dismissal query is loading, then `eligible` or
 * `ineligible`), and reads back which id is currently the winner.
 *
 * Winner selection is deterministic by render order: a later-JSX candidate
 * cannot win while an earlier-JSX candidate is still `pending`, even if it
 * resolves first. Once assigned, winnerId is locked for the session so
 * dismissing the winner does not immediately reveal the next promo.
 */
export const usePromoStore = create<PromoStore>((set) => ({
	candidates: [],
	winnerId: null,
	register: (id) =>
		set((state) => {
			if (state.candidates.some((candidate) => candidate.id === id)) {
				return state;
			}
			return { candidates: [...state.candidates, { id, status: 'pending' }] };
		}),
	unregister: (id) =>
		set((state) => ({
			candidates: state.candidates.filter((candidate) => candidate.id !== id),
		})),
	setStatus: (id, status) =>
		set((state) => {
			const nextCandidates = state.candidates.map((candidate) => (candidate.id === id ? { ...candidate, status } : candidate));
			const nextWinnerId = state.winnerId ?? computeWinner(nextCandidates);
			return { candidates: nextCandidates, winnerId: nextWinnerId };
		}),
}));
